/**
 * Spec №11 (11.3.1) — the App Shell "Place" list route (`/app/place/:entity`).
 * A thin wrapper: resolve the entity type + its default list view, render the
 * declared list. This is the destination a module's `view` sitemap target opens.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useEntityType } from "../../../lib/hooks/use-kuvio";
import { usePlaceViews } from "../../hooks/use-place-views";
import { useAppSitemap } from "../../hooks/use-app-sitemap";
import { DeclaredListView } from "./DeclaredListView";
import { CreateEntityModal } from "./CreateEntityModal";
import { TableSubjectHead } from "./TableSubjectHead";

export function PlaceListPage() {
  const { entity } = useParams<{ entity: string }>();
  const navigate = useNavigate();
  const type = useEntityType(entity);
  const views = usePlaceViews(entity);
  const sitemap = useAppSitemap();
  const [showCreate, setShowCreate] = useState(false);

  // №14.1.1-B — the page title is the PLACE's name, i.e. the same word the rail
  // uses. Entity types carry no `plural`, so the old `plural ?? name` rendered
  // the singular ("Contact", "Matter") as a list heading.
  const placeLabel = sitemap.data?.places.find((p) => p.view?.entity_type === entity)?.label;

  if (type.isLoading) {
    return (
      <div className="screen">
        <div className="card">
          <div className="skeleton sk-line" style={{ width: "30%" }} />
        </div>
      </div>
    );
  }
  if (!type.data) {
    return (
      <div className="screen">
        <div className="empty-state">
          <h3>Unknown place</h3>
          <p>No entity type "{entity}" is installed in this workspace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      {/* №29 (S2, mockup B) — the title and the primary verb live in the STAGE HEAD;
          the table is the stage. See TableSubjectHead. */}
      <TableSubjectHead
        title={placeLabel ?? type.data.plural ?? `${type.data.name}s`}
        actions={
          <button className="stagebtn stagebtn--primary" onClick={() => setShowCreate(true)}>
            New {type.data.name.toLowerCase()}
          </button>
        }
      />
      <DeclaredListView entityType={type.data} viewConfig={views.listView} />
      <CreateEntityModal
        entityType={type.data}
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSaved={(id) => { setShowCreate(false); navigate(`/app/place/${type.data!.slug}/${id}`); }}
      />
    </div>
  );
}
